'use client'

import { useState } from 'react'
import {
  ChevronDown,
  Shield,
  Lock,
  Eye,
  FileText,
  Globe,
  Users,
  AlertCircle,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import Hero from '@/components/Hero'
import Link from 'next/link'

export default function PrivacyPolicyPage() {
  const [openSections, setOpenSections] = useState<number[]>([0]) // First section open by default

  const toggleSection = (index: number) => {
    setOpenSections(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  const sections = [
    {
      title: 'Introduction',
      icon: Shield,
      content: (
        <>
          <p>
            Welcome to Pixelmojo (&quot;we,&quot; &quot;our,&quot; or
            &quot;us&quot;). We are committed to protecting your personal
            information and your right to privacy. This Privacy Policy explains
            how we collect, use, disclose, and safeguard your information when
            you visit our website{' '}
            <a
              href='https://www.pixelmojo.io'
              className='text-primary hover:underline font-semibold'
            >
              pixelmojo.io
            </a>{' '}
            (the &quot;Site&quot;) and use our services.
          </p>
          <p>
            By using our Site, you agree to the collection and use of
            information in accordance with this Privacy Policy. If you do not
            agree with our policies and practices, please do not use our Site.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      icon: FileText,
      content: (
        <>
          <h3 className='text-xl font-bold mb-3 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Personal Information You
            Provide
          </h3>
          <p>
            We collect personal information that you voluntarily provide to us
            when you:
          </p>
          <ul className='space-y-2 my-4'>
            {[
              'Fill out our contact form',
              'Use our AI chatbot assistant',
              'Subscribe to our newsletter',
              'Request a quote or book a consultation',
              'Communicate with us via email',
            ].map((item, i) => (
              <li key={i} className='flex gap-3 items-start'>
                <span className='mt-2 h-1.5 w-1.5 bg-primary flex-shrink-0' />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className='mb-3'>
            <strong>This information may include:</strong>
          </p>
          <div className='grid md:grid-cols-2 gap-3'>
            {[
              'Name (first and last)',
              'Email address',
              'Company name',
              'Phone number',
              'Project details and requirements',
              'Budget range and timeline preferences',
            ].map((item, i) => (
              <div
                key={i}
                className='flex gap-2 items-center p-3 bg-muted/30 border border-line'
              >
                <Check className='h-4 w-4 text-primary flex-shrink-0' />
                <span className='text-sm'>{item}</span>
              </div>
            ))}
          </div>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Automatically Collected
            Information
          </h3>
          <p className='mb-4'>
            When you visit our Site, we automatically collect certain
            information about your device and browsing behavior.
          </p>
          <div className='bg-card/50 border border-border p-6 space-y-2'>
            {[
              'IP address and geolocation data (country, region, city)',
              'Browser type and version',
              'Operating system',
              'Device information (mobile, desktop, tablet)',
              'Referring website',
              'Pages visited and time spent',
              'Click patterns and navigation paths',
              'Date and time of access',
            ].map((item, i) => (
              <div key={i} className='flex gap-2 items-start'>
                <span className='text-primary mt-1'>•</span>
                <span className='text-sm text-muted-foreground'>{item}</span>
              </div>
            ))}
          </div>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Chat Conversation Data
          </h3>
          <p className='mb-3'>
            When you interact with our AI chatbot, we collect and store:
          </p>
          <div className='grid md:grid-cols-2 gap-4'>
            {[
              { label: 'Chat messages', desc: 'Both yours and AI responses' },
              {
                label: 'Session metadata',
                desc: 'Page context, time on page, referrer',
              },
              {
                label: 'Engagement metrics',
                desc: 'Messages sent, session duration',
              },
              {
                label: 'Lead information',
                desc: 'Qualification and project details',
              },
            ].map((item, i) => (
              <div
                key={i}
                className='p-4 bg-primary/5 border-l-2 border-primary'
              >
                <p className='font-semibold text-sm text-foreground mb-1'>
                  {item.label}
                </p>
                <p className='text-xs text-muted-foreground'>{item.desc}</p>
              </div>
            ))}
          </div>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Service Diagnostics
          </h3>
          <p className='mb-4'>
            To operate our services reliably, we keep internal diagnostic
            records, including email delivery and failure logs, payment webhook
            events received from Paddle (billing metadata only — never card
            details), tool-run activity records, and provider error telemetry.
            Error telemetry is scrubbed of personal information before it is
            stored. Diagnostic records are accessible only to authorized
            administrators and are used only to operate, secure, and support the
            service.
          </p>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Cookies and Tracking
            Technologies
          </h3>
          <p className='mb-4'>
            We use cookies and similar tracking technologies to maintain
            sessions, analyze usage, and remember preferences.
          </p>
          <div className='space-y-3'>
            {[
              {
                type: 'Essential Cookies',
                desc: 'Required for site functionality (session management, authentication, remembering your consent choice and which regional consent policy applies)',
                color: 'primary',
              },
              {
                type: 'Analytics Cookies and Storage',
                desc: 'Help us understand website and product usage (Vercel Analytics, Google Analytics 4, Microsoft Clarity, PostHog)',
                color: 'secondary',
              },
              {
                type: 'Performance Cookies',
                desc: 'Monitor errors and performance (Sentry)',
                color: 'growth',
              },
            ].map((cookie, i) => (
              <div
                key={i}
                className={`p-4 border-l-4 border-${cookie.color} bg-${cookie.color}/5`}
              >
                <p className={`font-semibold text-${cookie.color} mb-1`}>
                  {cookie.type}
                </p>
                <p className='text-sm text-muted-foreground'>{cookie.desc}</p>
              </div>
            ))}
          </div>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Analytics and Regional
            Consent
          </h3>
          <p className='mb-4'>
            Analytics information covers how our website and the Radar product
            are used: pages viewed, general device and browser characteristics,
            approximate location, and product events such as starting an audit
            or exporting a report. We use it to understand website and product
            usage, diagnose technical and product issues, measure performance,
            evaluate user journeys and conversion behavior, and improve the
            website and the Radar product. The analytics providers that may
            process this information are Google Analytics 4, Microsoft Clarity,
            PostHog, and Vercel Analytics (which is cookieless and cannot
            identify you).
          </p>
          <p className='mb-4'>
            Depending on your location, we apply different requirements to
            analytics technologies. Where applicable law requires prior consent,
            including the EEA, the United Kingdom, and Switzerland,
            non-essential analytics technologies remain disabled until you
            provide permission. In other regions, analytics may be active when
            you access the website, and you may opt out at any time through the
            Cookie Settings link in the footer of every page. A previously
            recorded rejection or opt-out will always be respected, regardless
            of the regional default. If we cannot determine your location, we
            apply the prior-consent rule.
          </p>
          <p className='mb-4'>
            Where prior consent is required, your consent is our legal basis for
            analytics processing, and you can withdraw it at any time through
            Cookie Settings. In other regions we process analytics information
            on the basis of our legitimate interest in understanding and
            improving our website and products, where permitted by applicable
            law, always combined with clear notice and the ability to opt out.
            Full details of each analytics service, what it stores, and for how
            long are in our{' '}
            <Link
              href='/cookie-policy'
              className='text-primary hover:underline font-semibold'
            >
              Cookie Policy
            </Link>
            .
          </p>
          <p className='mb-4'>
            Separately from these analytics services, we keep a small
            first-party record of consent banner activity so we can verify the
            consent controls work, measure whether visitors can successfully
            make privacy choices, diagnose failures in the consent interface,
            and distinguish banner exposure from acceptance, rejection, and
            opt-out. Each record contains exactly three fields: the event type
            (banner_shown, accepted, rejected, opted_out, preference_changed, or
            policy_applied), the regional policy that applied (prior-consent or
            notice-opt-out), and a server-generated timestamp. No email address,
            user or account identifier, session identifier, IP address, user
            agent, page URL, or free text is recorded, so these records cannot
            be linked to you and are not user-level tracking.
          </p>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      icon: Eye,
      content: (
        <>
          <p className='mb-4'>We use the information we collect to:</p>
          <div className='grid md:grid-cols-2 gap-3'>
            {[
              'Respond to your inquiries and provide customer support',
              'Process your requests for quotes, consultations, or services',
              'Send you project updates and communication',
              'Improve our website, products, and services',
              'Analyze usage patterns and optimize user experience',
              'Send marketing communications (with your consent)',
              'Detect and prevent fraud or security issues',
              'Comply with legal obligations',
              'Personalize your experience on our Site',
            ].map((item, i) => (
              <div
                key={i}
                className='flex gap-3 items-start p-3 bg-muted/20 border border-border/30 hover:border-primary/50 transition-colors'
              >
                <span className='text-lg'>{i + 1}.</span>
                <span className='text-sm'>{item}</span>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      title: 'Radar Platform Data',
      icon: Globe,
      content: (
        <>
          <h3 className='text-xl font-bold mb-3 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Data We Collect Through
            Radar
          </h3>
          <p className='mb-4'>
            When you use Radar by Pixelmojo, we collect: the domain name you
            submit for auditing, your email address (if you request an access
            token or email report), your brand name (if provided), and your
            audit results including tool scores, action items, and insights.
          </p>
          <p className='mb-4'>
            We do not collect or store payment card information. All payments
            are processed by{' '}
            <a
              href='https://paddle.com'
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline font-semibold'
            >
              Paddle.com
            </a>
            , our Merchant of Record.
          </p>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Third-Party AI Services
          </h3>
          <p className='mb-4'>
            Radar queries the following third-party AI services on your behalf
            to perform citation and visibility analysis:
          </p>
          <div className='grid md:grid-cols-2 gap-3 mb-4'>
            {[
              {
                name: 'OpenAI (ChatGPT)',
                use: 'Citation and readiness analysis',
              },
              { name: 'Anthropic (Claude)', use: 'Citation tracking' },
              { name: 'Google (Gemini)', use: 'Citation tracking' },
              { name: 'Perplexity AI', use: 'Citation and source analysis' },
            ].map((service, i) => (
              <div
                key={i}
                className='flex gap-2 items-center p-3 bg-muted/30 border border-line'
              >
                <Check className='h-4 w-4 text-primary flex-shrink-0' />
                <div>
                  <span className='text-sm font-semibold'>{service.name}</span>
                  <span className='text-xs text-muted-foreground ml-2'>
                    {service.use}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className='mb-4'>
            Your submitted domain name is sent to these services as part of the
            audit process. We also scan publicly available Reddit and YouTube
            content to surface brand mentions. We do not sell or share your data
            with these services beyond what is required to perform the audit.
          </p>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Radar Data Retention
          </h3>
          <p className='mb-4'>
            Audit results are retained while the associated report or account
            remains active; some free-report links expire and become
            inaccessible after approximately 30 days. You may request deletion
            of your audit data at any time by emailing{' '}
            <a
              href='mailto:founders@pixelmojo.io'
              className='text-primary hover:underline font-semibold'
            >
              founders@pixelmojo.io
            </a>
            .
          </p>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Payments
          </h3>
          <p>
            Payments for Radar credits are processed by{' '}
            <a
              href='https://paddle.com'
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline font-semibold'
            >
              Paddle.com
            </a>
            , who acts as the Merchant of Record. Pixelmojo does not store or
            process your payment card information. For billing inquiries,
            contact Paddle support directly. For all other payment questions,
            email{' '}
            <a
              href='mailto:founders@pixelmojo.io'
              className='text-primary hover:underline font-semibold'
            >
              founders@pixelmojo.io
            </a>
            .
          </p>
        </>
      ),
    },
    {
      title: 'Radar Chrome Extension',
      icon: Globe,
      content: (
        <>
          <h3 className='text-xl font-bold mb-3 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> What the Extension Reads
          </h3>
          <p className='mb-4'>
            When you click the Radar icon in your browser toolbar, the extension
            reads only the URL of the active tab so the audit can run against
            the site you are currently viewing. Only the domain portion (for
            example, <code>example.com</code>) is sent to pixelmojo.io. The
            extension does not read page content, the DOM, scripts, cookies,
            local storage of the audited site, other open tabs, or browsing
            history.
          </p>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Local Storage on Your Device
          </h3>
          <p className='mb-4'>
            The extension uses <code>chrome.storage.local</code> to store two
            things on your device only: an anonymous, randomly generated usage
            fingerprint, and a small audit history (domain plus score) so you
            can revisit prior results. The fingerprint enforces the free-tier
            rate limit of one audit per domain consistently across browser
            sessions. It is not derived from any personal identifier and is
            never combined with PII.
          </p>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> What the Extension Does Not
            Collect
          </h3>
          <div className='bg-card/50 border border-border p-6 space-y-2'>
            {[
              'Page content (HTML, text, images, video, scripts)',
              'Browsing history beyond the single URL you explicitly audit',
              'Personally identifiable information (no name, email, or account)',
              'Authentication credentials (the extension has no sign-in)',
              'Cookies or session tokens from the audited site',
              'Keystrokes, mouse movement, scroll position, or click patterns',
              'Data from any tab other than the one you actively audit',
            ].map((item, i) => (
              <div key={i} className='flex gap-2 items-start'>
                <span className='text-primary mt-1'>•</span>
                <span className='text-sm text-muted-foreground'>{item}</span>
              </div>
            ))}
          </div>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Single Purpose
          </h3>
          <p className='mb-4'>
            The Radar Chrome Extension exists for one purpose: running an AI
            search visibility audit on the domain of the active browser tab. It
            does not contact third-party APIs from the browser, does not load
            remote code, and does not include any analytics or tracking SDKs.
            All data flows go directly between the extension and the Radar audit
            API at pixelmojo.io.
          </p>

          <h3 className='text-xl font-bold mb-3 mt-8 text-foreground flex items-center gap-2'>
            <span className='text-primary'>→</span> Uninstall and Data Deletion
          </h3>
          <p>
            Uninstalling the extension from <code>chrome://extensions</code>{' '}
            removes all locally stored data (fingerprint and audit history).
            Audit results retained on pixelmojo.io are linked to the anonymous
            fingerprint, not to your identity, and follow the retention criteria
            noted in the Radar Platform Data section above. To request deletion
            of audit data associated with a specific fingerprint, email{' '}
            <a
              href='mailto:founders@pixelmojo.io'
              className='text-primary hover:underline font-semibold'
            >
              founders@pixelmojo.io
            </a>
            .
          </p>
        </>
      ),
    },
    {
      title: 'Ask API and MCP Server',
      icon: Globe,
      content: (
        <>
          <p className='mb-4'>
            Our public Ask API and MCP integration answer questions using
            published Pixelmojo information. They do not require an account or
            retrieve private customer or account records. OpenAI processes your
            sanitized question to find relevant content and, when context is
            available, processes the question and that public content to
            generate an answer. Sanitization removes markup; it does not
            anonymize any personal information you include in your question.
          </p>
          <p className='mb-4'>
            In our Ask database logs, MCP question text is replaced by a fixed
            placeholder. Questions sent through the REST Ask API are stored
            sanitized and truncated to 500 characters. These records store the
            answer length, not the generated answer text. Supabase stores these
            records with operational metadata, including outcome, latency,
            matched-source counts, confidence, model, request method and
            channel, user agent, and a keyed IP pseudonym. Raw IP addresses are
            not stored in these Ask log records.
          </p>
          <p className='mb-4'>
            Separately, Upstash processes the trusted client IP address in
            rate-limit counter keys. These counters currently limit Ask
            executions to 10 per minute and 50 per day across REST and MCP, and
            expire after their 60-second or 24-hour window. Clients that share
            an IP address share these limits.
          </p>
          <p>
            We do not currently run an automatic purge of Ask database logs. The
            retention criteria and deletion rights elsewhere in this policy
            apply to these records. This describes our application logs; your
            chosen AI client and our service providers have their own data
            handling policies. Contact{' '}
            <a
              href='mailto:privacy@pixelmojo.io'
              className='text-primary hover:underline font-semibold'
            >
              privacy@pixelmojo.io
            </a>{' '}
            for data requests.
          </p>
        </>
      ),
    },
    {
      title: 'Third-Party Services and Data Sharing',
      icon: Users,
      content: (
        <>
          <p className='mb-6'>
            We use trusted third-party services to operate our Site and provide
            our services. These services may have access to your personal
            information only to perform specific tasks on our behalf.
          </p>

          <h3 className='text-xl font-bold mb-4 text-foreground'>
            Service Providers We Use
          </h3>
          <div className='grid gap-4'>
            {[
              {
                name: 'Vercel',
                desc: 'Web hosting and deployment',
                url: 'https://vercel.com/legal/privacy-policy',
              },
              {
                name: 'Supabase',
                desc: 'Database and backend services',
                url: 'https://supabase.com/privacy',
              },
              {
                name: 'OpenAI',
                desc: 'AI chatbot, public Ask/MCP retrieval and answer generation',
                url: 'https://openai.com/privacy',
              },
              {
                name: 'Sentry',
                desc: 'Error monitoring and performance tracking',
                url: 'https://sentry.io/privacy',
              },
              {
                name: 'Google Analytics 4 (gtag.js)',
                desc: 'Web analytics (runs only when analytics is permitted for you: your consent, or notice-and-opt-out where applicable)',
                url: 'https://policies.google.com/privacy',
              },
              {
                name: 'Microsoft Clarity',
                desc: 'Session analytics (heatmaps and interaction analytics; runs only when analytics is permitted for you)',
                url: 'https://privacy.microsoft.com/privacystatement',
              },
              {
                name: 'PostHog',
                desc: 'Product analytics for the Radar platform (limited anonymous product events; runs only when analytics is permitted for you)',
                url: 'https://posthog.com/privacy',
              },
              {
                name: 'ipapi.co',
                desc: 'IP-based geolocation for chat personalization',
                url: 'https://ipapi.co/privacy/',
              },
              {
                name: 'Upstash',
                desc: 'Rate limiting and service reliability (short-lived counters keyed by email/IP)',
                url: 'https://upstash.com/trust/privacy.pdf',
              },
              {
                name: 'Paddle',
                desc: 'Payment processing (Merchant of Record)',
                url: 'https://www.paddle.com/legal/privacy',
              },
              {
                name: 'Resend',
                desc: 'Transactional and marketing email delivery',
                url: 'https://resend.com/legal/privacy-policy',
              },
            ].map((service, i) => (
              <div
                key={i}
                className='p-5 bg-card/50 border border-border hover:border-primary/50 transition-all'
              >
                <div className='flex justify-between items-start mb-2'>
                  <div>
                    <h4 className='font-bold text-foreground'>
                      {service.name}
                    </h4>
                    <p className='text-sm text-muted-foreground mt-1'>
                      {service.desc}
                    </p>
                  </div>
                  <a
                    href={service.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-xs text-primary hover:underline whitespace-nowrap ml-4'
                  >
                    Privacy Policy →
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className='mt-8 p-6 bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-500 dark:border-yellow-400'>
            <h4 className='font-bold text-yellow-900 dark:text-yellow-100 mb-2 flex items-center gap-2'>
              <AlertCircle className='h-5 w-5 text-yellow-700 dark:text-yellow-400' />
              International Data Transfers
            </h4>
            <p className='text-sm text-yellow-800 dark:text-yellow-200'>
              Your information may be transferred to and processed in countries
              other than the Philippines, including the United States and
              Europe. We ensure appropriate safeguards are in place to protect
              your data in accordance with this Privacy Policy.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Data Retention',
      icon: Lock,
      content: (
        <>
          <p className='mb-6'>
            We retain your personal information only for as long as necessary to
            fulfill the purposes outlined in this Privacy Policy, unless a
            longer retention period is required by law.
          </p>
          <p className='mb-4'>
            Rather than fixed periods, we apply retention criteria:
          </p>
          <div className='grid gap-4 mb-6'>
            {[
              {
                type: 'Inquiries and project data',
                note: 'Retained while your inquiry, project, or business relationship with us remains active, and afterwards as needed for dispute resolution and record-keeping.',
              },
              {
                type: 'Chat conversations',
                note: 'Retained while relevant to supporting you and improving our AI assistant, or until you request deletion.',
              },
              {
                type: 'Newsletter and marketing subscriptions',
                note: 'Retained until you unsubscribe; suppression records are kept so we honor your opt-out.',
              },
              {
                type: 'Radar audit results',
                note: 'Retained while the associated report or account remains active; some free-report links expire and become inaccessible after approximately 30 days.',
              },
              {
                type: 'Billing and diagnostic records',
                note: 'Retained as needed for accounting, tax, fraud-prevention, and service-reliability purposes.',
              },
            ].map((item, i) => (
              <div key={i} className='p-4 bg-muted/30 border border-border'>
                <p className='font-semibold text-foreground'>{item.type}</p>
                <p className='text-xs text-muted-foreground mt-1'>
                  {item.note}
                </p>
              </div>
            ))}
          </div>
          <p>
            You may request deletion of your personal information at any time by
            emailing{' '}
            <a
              href='mailto:privacy@pixelmojo.io'
              className='text-primary hover:underline font-semibold'
            >
              privacy@pixelmojo.io
            </a>
            . We action verified requests within 30 days.
          </p>
        </>
      ),
    },
    {
      title: 'Your Privacy Rights',
      icon: Users,
      content: (
        <>
          <p className='mb-6'>
            Depending on your location, you may have the following rights
            regarding your personal information:
          </p>
          <div className='space-y-4'>
            {[
              {
                right: 'Right to Access',
                desc: 'Request a copy of the personal information we hold about you',
              },
              {
                right: 'Right to Rectification',
                desc: 'Request correction of inaccurate or incomplete information',
              },
              {
                right: 'Right to Erasure',
                desc: 'Request deletion of your personal information',
              },
              {
                right: 'Right to Restrict Processing',
                desc: 'Request limitation on how we use your data',
              },
              {
                right: 'Right to Data Portability',
                desc: 'Receive your data in a structured, machine-readable format',
              },
              {
                right: 'Right to Object',
                desc: 'Object to processing of your personal information',
              },
              {
                right: 'Right to Withdraw Consent',
                desc: 'Withdraw consent for marketing communications, and for analytics at any time via the Cookie Settings link in the footer',
              },
            ].map((item, i) => (
              <div
                key={i}
                className='flex gap-4 p-4 bg-card/50 border border-border'
              >
                <span className='flex h-10 w-10 items-center justify-center bg-primary/10 text-primary font-bold text-lg flex-shrink-0'>
                  {i + 1}
                </span>
                <div>
                  <p className='font-bold text-foreground'>{item.right}</p>
                  <p className='text-sm text-muted-foreground mt-1'>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className='mt-6'>
            We do not sell or share your personal information, including as
            &quot;sale&quot; and &quot;sharing&quot; are defined under the
            California Consumer Privacy Act.
          </p>
          <div className='mt-6 p-6 bg-primary/5 border-l-4 border-primary'>
            <p className='text-sm'>
              To exercise these rights, please contact us at{' '}
              <a
                href='mailto:privacy@pixelmojo.io'
                className='text-primary hover:underline font-semibold'
              >
                privacy@pixelmojo.io
              </a>
              . We will respond to your request within <strong>30 days</strong>.
            </p>
          </div>
          <div className='mt-4 p-6 bg-primary/5 border-l-4 border-primary'>
            <p className='text-sm'>
              <strong>We will never ask for your secrets.</strong> Pixelmojo
              will never ask you for passwords, API keys, access tokens, OAuth
              refresh tokens, payment card numbers, or other secrets through
              support email or the site chat. Do not share credentials with
              anyone claiming to represent us.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Governing Law',
      icon: Globe,
      content: (
        <>
          <p className='mb-4'>
            This Privacy Policy is governed by the laws of the{' '}
            <strong>Republic of the Philippines</strong>, including the Data
            Privacy Act of 2012 (Republic Act No. 10173).
          </p>
          <p>
            If you have questions or complaints regarding your data privacy
            rights under Philippine law, you may contact the National Privacy
            Commission (NPC) at{' '}
            <a
              href='https://www.privacy.gov.ph'
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline font-semibold'
            >
              privacy.gov.ph
            </a>
            .
          </p>
        </>
      ),
    },
  ]

  return (
    <div className='container mx-auto px-4 py-16 animate-fade-in'>
      {/* Hero Section */}
      <Hero
        title={<>Privacy Policy</>}
        description='How we collect, use, and protect your personal information. Last updated: September 6, 2026'
        eyebrow='Legal'
        ctaText='Contact Us'
        ctaHref='/contact-us'
      />

      {/* Quick Navigation */}
      <LenisFadeIn delay={200}>
        <section className='max-w-6xl mx-auto mb-16'>
          <div className='bg-card/50 border border-border p-8'>
            <h2 className='text-xl font-bold mb-4 text-foreground'>
              Quick Navigation
            </h2>
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-3'>
              {sections.map((section, i) => {
                const Icon = section.icon
                return (
                  <button
                    key={i}
                    onClick={() => toggleSection(i)}
                    className='flex items-center gap-3 p-3 bg-muted/30 hover:bg-primary/10 border border-border hover:border-primary/50 transition-all text-left group'
                  >
                    <Icon className='h-5 w-5 text-primary' />
                    <span className='text-sm font-medium group-hover:text-primary transition-colors'>
                      {section.title}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </section>
      </LenisFadeIn>

      {/* Accordion Sections */}
      <LenisStaggered delay={300} staggerDelay={100}>
        <div className='max-w-6xl mx-auto space-y-4 mb-32'>
          {sections.map((section, index) => {
            const Icon = section.icon
            const isOpen = openSections.includes(index)

            return (
              <div
                key={index}
                className='border-2 border-border bg-card/70 hover:border-primary/30 transition-all'
              >
                <button
                  onClick={() => toggleSection(index)}
                  className='w-full p-6 md:p-8 flex items-center justify-between text-left group'
                >
                  <div className='flex items-center gap-4'>
                    <div className='flex h-12 w-12 items-center justify-center bg-primary/10 group-hover:bg-primary/20 transition-colors'>
                      <Icon className='h-6 w-6 text-primary' />
                    </div>
                    <div>
                      <span className='text-xs uppercase tracking-wider text-muted-foreground block mb-1'>
                        Section {index + 1}
                      </span>
                      <h3 className='text-xl md:text-2xl font-bold text-foreground group-hover:text-primary transition-colors'>
                        {section.title}
                      </h3>
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-6 w-6 text-muted-foreground transition-transform duration-300 ease-in-out flex-shrink-0 ${
                      isOpen ? 'rotate-180 text-primary' : ''
                    }`}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className='px-6 md:px-8 pb-8 pt-4 border-t border-border prose prose-lg dark:prose-invert max-w-none'>
                    {section.content}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </LenisStaggered>

      {/* Contact Section */}
      <LenisFadeIn delay={500}>
        <section className='max-w-4xl mx-auto mb-32'>
          <div className='border-2 border-primary p-10 bg-primary/5'>
            <div className='flex items-start gap-6 mb-6'>
              <div className='flex h-16 w-16 items-center justify-center bg-primary/20 flex-shrink-0'>
                <Shield className='h-8 w-8 text-primary' />
              </div>
              <div>
                <h2 className='text-3xl font-bold mb-3 text-foreground'>
                  Have Questions About Your Data?
                </h2>
                <p className='text-muted-foreground mb-6'>
                  We're here to help. Contact our privacy team for any
                  questions, concerns, or requests.
                </p>
              </div>
            </div>
            <div className='grid md:grid-cols-2 gap-4'>
              <div className='p-5 bg-background/50 border border-border'>
                <p className='text-sm font-semibold text-muted-foreground mb-2'>
                  Privacy Inquiries
                </p>
                <a
                  href='mailto:privacy@pixelmojo.io'
                  className='text-lg font-bold text-primary hover:underline'
                >
                  privacy@pixelmojo.io
                </a>
              </div>
              <div className='p-5 bg-background/50 border border-border'>
                <p className='text-sm font-semibold text-muted-foreground mb-2'>
                  General Contact
                </p>
                <a
                  href='mailto:founders@pixelmojo.io'
                  className='text-lg font-bold text-primary hover:underline'
                >
                  founders@pixelmojo.io
                </a>
              </div>
            </div>
            <div className='mt-4 p-5 bg-background/50 border border-border'>
              <p className='text-sm font-semibold text-muted-foreground mb-2'>
                Physical Address
              </p>
              <p className='text-foreground'>
                111 Paseo de Roxas, Legazpi Village, Makati, 1229 Metro Manila,
                Philippines
              </p>
            </div>
          </div>
        </section>
      </LenisFadeIn>

      {/* Related Links */}
      <LenisFadeIn delay={600}>
        <section className='max-w-4xl mx-auto'>
          <div className='bg-muted/30 border border-border p-8 text-center'>
            <p className='text-sm text-muted-foreground mb-4'>
              Related policies
            </p>
            <div className='flex flex-col sm:flex-row gap-4 justify-center'>
              <Link
                href='/terms-of-service'
                className='inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-all'
              >
                Terms of Service →
              </Link>
              <Link
                href='/refund-policy'
                className='inline-flex items-center gap-2 px-6 py-3 bg-muted hover:bg-muted/80 border border-border text-foreground font-semibold transition-all'
              >
                Refund Policy →
              </Link>
            </div>
          </div>
        </section>
      </LenisFadeIn>
    </div>
  )
}

function Check({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox='0 0 20 20' fill='currentColor'>
      <path
        fillRule='evenodd'
        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
        clipRule='evenodd'
      />
    </svg>
  )
}
